import turfBbox from '@turf/bbox'
import {
  FitBoundsOptions,
  GeoJSONSource,
  LngLatBoundsLike,
  LngLatLike,
  Map as MaplibreMap,
  MapGeoJSONFeature,
  Marker as MaplibreMarker,
} from 'maplibre-gl'
import { FeatureCollection, GeoJsonProperties, Geometry, Feature } from 'geojson'
import { MapLayer, MapMarker, MapSource } from './types'
import { getEmptyFeatureCollection } from '../../api-mappings/feature-collection'
import { MAX_ZOOM_FOR_CLUSTER_FORCED_ZOOM } from './constants'
import { extendBounds, isNonExpandedCluster } from './utils-lite'

/**
 * ----------------------------------------------------------------------------------------------------------------
 *
 *              ██╗    ██╗ █████╗ ██████╗ ███╗   ██╗██╗███╗   ██╗ ██████╗
 *              ██║    ██║██╔══██╗██╔══██╗████╗  ██║██║████╗  ██║██╔════╝
 *              ██║ █╗ ██║███████║██████╔╝██╔██╗ ██║██║██╔██╗ ██║██║  ███╗
 *              ██║███╗██║██╔══██║██╔══██╗██║╚██╗██║██║██║╚██╗██║██║   ██║
 *              ╚███╔███╔╝██║  ██║██║  ██║██║ ╚████║██║██║ ╚████║╚██████╔╝
 *               ╚══╝╚══╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═══╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 *      This file should only contain utils that are required for the full maplibre map object because using
 *      those utils will cause a full load of the maplibre library. This is fine if the map is loaded but we
 *      want to avoid loading the entire library if we're just using a simple util that only uses an
 *      interface/type from the maplibre library.
 *
 *      If you need to add a util that only uses an interface/type from the maplibre library, add it to the
 *      utils-lite.ts file.
 *
 * ----------------------------------------------------------------------------------------------------------------
 */

export function getSource(map: MaplibreMap | null, sourceType: MapSource) {
  return map?.getSource(sourceType) as GeoJSONSource
}

export function setLayerVisibility(map: MaplibreMap, layer: MapLayer, visible: boolean) {
  map.setLayoutProperty(layer, 'visibility', visible ? 'visible' : 'none')
}

/**
 * Sets the source data for a layer on the map.
 * @param map The map that has the layers.
 * @param source The source ID for the data.
 * @param data The new data to set.
 */
export function setSourceData(
  map: MaplibreMap | null,
  source: MapSource,
  data: FeatureCollection<Geometry, GeoJsonProperties> | undefined
) {
  const layerSource = getSource(map, source)
  if (data && data.features.length) {
    layerSource.setData(data)
  } else {
    layerSource.setData(getEmptyFeatureCollection())
  }
}

/**
 * Helper to set the bounds on the map. Needed because there is an issue with the typings for fitBounds.
 * @param map Instance of the maplibre Map to fit the bounds.
 * @param bounds Array containing the sw and ne coordinates of the bounds to set.
 * @param duration The animation time to fit the bounds.
 */
export function fitMapBounds(
  map?: MaplibreMap | null,
  bounds?: LngLatBoundsLike | null,
  options?: FitBoundsOptions | undefined
) {
  if (!map || !bounds) {
    return
  }

  map.fitBounds(bounds, options)
}

export function fitMapBoundsToFeature(map: MaplibreMap, feature: Feature, duration: number) {
  const bbox = turfBbox(feature)
  const sw = { lng: bbox[0], lat: bbox[1] }
  const ne = { lng: bbox[2], lat: bbox[3] }

  fitMapBounds(map, [sw, ne], { duration })
}

export function canZoomOnCluster(map: MaplibreMap, feature: MapGeoJSONFeature, layerId: MapLayer) {
  if (map.getZoom() > MAX_ZOOM_FOR_CLUSTER_FORCED_ZOOM) {
    return false
  }

  // We only support small cluster zoom on the original clusters (e.g. - non-expanded meaning the top level
  // clusters that are returned from the back end).
  if (!isNonExpandedCluster(feature)) {
    return false
  }

  // Have to be on the relevant layer.
  if ((feature.layer.id as MapLayer) !== layerId) {
    return false
  }

  return true
}

/**
 * Helper to create a maplibre marker.
 * @param domElement The DOM element that will be used to display the marker.
 * @param coord The lng/lat coordinates for the marker.
 * @param map The map that will host the marker.
 */
export function createMaplibreMarker(
  domElement: HTMLDivElement,
  coord: LngLatLike,
  map: MaplibreMap,
  onMarkerClick?: (this: HTMLElement, ev: MouseEvent) => void,
  classList?: string[]
) {
  const marker = new MaplibreMarker({ offset: [-2, -14], element: domElement })
  marker.setLngLat(coord).addTo(map)

  if (onMarkerClick) {
    // Reference: https://stackoverflow.com/a/45887382
    marker.getElement().addEventListener('click', onMarkerClick)
  }

  if (classList?.length) {
    marker.getElement().classList.add(...classList)
  }

  return marker
}

/**
 * Helper that calculates a bounds that encompasses all the passed-in markers.
 * @param markers
 * @returns
 */
export function getMakersExtendedBounds(markers: MapMarker<unknown>[] | undefined): LngLatBoundsLike | undefined {
  let bounds: LngLatBoundsLike | undefined = undefined
  markers?.forEach((m) => {
    bounds = extendBounds(bounds, m.coordinate.lng, m.coordinate.lat)
  })

  return bounds
}

/**
 * Helper to create a DOM element that will be used as the base marker (e.g - a simple pin on the map).
 * @param color The color for the marker
 */
export const createBaseMarkerElement = (color: string) => {
  const el = document.createElement('div')
  el.innerHTML = `<svg width="24" height="30" viewBox="0 0 23 27" fill="none" xmlns="http://www.w3.org/2000/svg">
<mask id="path-1-outside-1_10442_23411" maskUnits="userSpaceOnUse" x="-0.76001" y="0" width="25" height="28" fill="black">
<rect fill="white" x="-0.76001" width="24" height="30"/>
<path d="M20.5107 6.38544C17.9824 1.56737 11.8541 -0.380926 6.82508 2.04128C1.79602 4.46348 -0.155149 10.5453 2.29068 15.1528C3.44489 17.338 8.2541 23.0249 10.3701 25.5261C10.8923 26.158 11.8816 26.158 12.4312 25.5261C14.5473 23.0249 19.3015 17.338 20.5382 15.1528C22.0771 12.4673 22.0222 9.25522 20.5107 6.38544Z"/>
</mask>
<path d="M20.5107 6.38544C17.9824 1.56737 11.8541 -0.380926 6.82508 2.04128C1.79602 4.46348 -0.155149 10.5453 2.29068 15.1528C3.44489 17.338 8.2541 23.0249 10.3701 25.5261C10.8923 26.158 11.8816 26.158 12.4312 25.5261C14.5473 23.0249 19.3015 17.338 20.5382 15.1528C22.0771 12.4673 22.0222 9.25522 20.5107 6.38544Z" fill="${color}"/>
<path d="M20.5107 6.38544L19.6252 6.8501L19.6259 6.85144L20.5107 6.38544ZM2.29068 15.1528L3.17492 14.6857L3.17394 14.6839L2.29068 15.1528ZM10.3701 25.5261L11.1411 24.8891L11.1336 24.8802L10.3701 25.5261ZM12.4312 25.5261L13.1858 26.1824L13.1947 26.172L12.4312 25.5261ZM20.5382 15.1528L19.6705 14.6555L19.6679 14.6602L20.5382 15.1528ZM21.3962 5.92078C18.6074 0.606193 11.8858 -1.50613 6.39114 1.14033L7.25901 2.94222C11.8225 0.744279 17.3575 2.52854 19.6252 6.8501L21.3962 5.92078ZM6.39114 1.14033C0.866157 3.80139 -1.31256 10.4977 1.40741 15.6216L3.17394 14.6839C1.00226 10.5929 2.72588 5.12556 7.25901 2.94222L6.39114 1.14033ZM1.40644 15.6198C2.03702 16.8136 3.59516 18.8598 5.18981 20.8468C6.81011 22.8658 8.54936 24.9222 9.60671 26.172L11.1336 24.8802C10.0749 23.6288 8.35151 21.5911 6.74963 19.5951C5.12211 17.567 3.69855 15.6771 3.17491 14.6857L1.40644 15.6198ZM9.59928 26.1631C10.5314 27.291 12.248 27.2605 13.1857 26.1824L11.6767 24.8698C11.5152 25.0555 11.2532 25.0249 11.141 24.8891L9.59928 26.1631ZM13.1947 26.172C14.2546 24.9191 15.979 22.8641 17.5951 20.846C19.1901 18.8542 20.743 16.8212 21.4085 15.6453L19.6679 14.6602C19.0967 15.6695 17.6541 17.5727 16.0339 19.5959C14.4347 21.5929 12.7239 23.6319 11.6678 24.8802L13.1947 26.172ZM21.4058 15.65C23.1332 12.6356 23.0486 9.05809 21.3955 5.91944L19.6259 6.85144C20.9958 9.45235 21.0211 12.2989 19.6706 14.6555L21.4058 15.65Z" fill="white" mask="url(#path-1-outside-1_10442_23411)"/>
<path d="M11.4144 14.5733C9.2357 14.5733 7.48459 12.8762 7.48459 10.8084C7.48459 8.7211 9.2357 7.04346 11.4144 7.04346C13.5931 7.04346 15.3442 8.74061 15.3442 10.8084C15.3646 12.8762 13.5931 14.5733 11.4144 14.5733Z" fill="white"/>
</svg>

  `

  return el
}
